import { BUILDING_TYPE_KEYS, BUILDING_TYPES, patternPool, type BuildingTypeKey } from '@nsb/core';

/**
 * What is being built, in words (spec "Pattern Mapping" §4.2): each building type with how many patterns it could
 * touch, how many there are to pick from, and a type that already takes in another's.
 */

export interface BuildingRow {
  key: BuildingTypeKey;
  name: string;
  ticked: boolean;
  /** How many patterns it could touch on its own. */
  count: number;
}

export function buildingRows(types: readonly BuildingTypeKey[]): BuildingRow[] {
  return BUILDING_TYPE_KEYS.map((k) => ({ key: k, name: BUILDING_TYPES[k].name, ticked: types.includes(k), count: patternPool([k]).length }));
}

/** How many patterns there are to pick from; nothing ticked leaves the step Not configured, until it is done. */
export function poolLine(types: readonly BuildingTypeKey[], done = false): string {
  if (!types.length) return done ? 'Nothing ticked yet.' : 'Nothing ticked yet: the step is Not configured until something is.';
  return `${patternPool(types).length} patterns to pick from.`;
}

const lower = (k: BuildingTypeKey) => BUILDING_TYPES[k].name.replace(/^A /, 'a ');

/** A ticked type whose patterns another ticked type already takes in, said once: ticking it changes nothing. */
export function coveredLine(types: readonly BuildingTypeKey[]): string | null {
  for (const big of types) {
    const pool = new Set(patternPool([big]));
    const inside = types.filter((k) => k !== big && patternPool([k]).every((n) => pool.has(n)));
    if (inside.length) return `${BUILDING_TYPES[big].name} already takes in every pattern ${inside.map(lower).join(' or ')} could touch.`;
  }
  return null;
}

/** The types with one ticked or unticked, in their own order. */
export function toggled(types: readonly BuildingTypeKey[], key: BuildingTypeKey, on: boolean): BuildingTypeKey[] {
  return BUILDING_TYPE_KEYS.filter((k) => (k === key ? on : types.includes(k)));
}
